import { Col, Row } from 'react-grid-system'
import cx from 'classnames'
import styles from './CardGroup.module.scss'
import { getResponsiveCarouselSettings } from 'utils/carousel/getResponsiveCarouselSettings'
import { useWindowSize } from 'utils/hooks'
import { Card, CarouselV2, Heading } from '~elements'
import { CardGroupType } from '~types'

const CardGroup = ({
  backgroundGradient,
  backgroundColor,
  cards,
  cardsHaveLargeFontSizes = false,
  centerCopyOnMobile,
  hasDescriptionListCheckMark,
  headline,
  isCarousel,
  isCarouselMobileOnly,
  stackMediaAndCopy,
  subheadlineColor,
  slidesToShow = 3
}: CardGroupType) => {
  const { isBreakpoint: isBelowMd } = useWindowSize('sm', '<')
  const hasOneItemAndStackMedia = stackMediaAndCopy && cards?.length === 1
  const itemsLength = hasOneItemAndStackMedia ? 3 : cards?.length
  const colSpan = stackMediaAndCopy ? Math.max(12 / Math.min(itemsLength ?? 1, 3), 3) : 12
  const hasHeadlineTitle = headline && headline?.title && (!isCarousel || (isCarouselMobileOnly && !isBelowMd))
  const shouldShowAsCarousel = (isCarousel && !isCarouselMobileOnly) || (isCarouselMobileOnly && isBelowMd)
  const slidesToShowCalculated = stackMediaAndCopy ? slidesToShow : 1
  const responsive = getResponsiveCarouselSettings(slidesToShowCalculated, {
    sm: {
      dots: true
    }
  })

  return (
    <div
      className={cx(
        styles['card-group'],
        shouldShowAsCarousel && styles['card-group--peek-next-carousel-slide'],
        styles['card-group__header']
      )}
    >
      {hasHeadlineTitle && (
        <div className={styles[`card-group__headline`]}>
          <Heading {...headline} noMarginBottom noMarginTop>
            {headline.title}
          </Heading>
        </div>
      )}
      <Row
        style={{
          ...(shouldShowAsCarousel && {
            marginRight: 0,
            marginLeft: 0,
            display: 'block'
          }),
          rowGap: isBelowMd ? '24px' : '32px'
        }}
      >
        {shouldShowAsCarousel ? (
          <CarouselV2
            slides={cards ?? []}
            showArrowsBreakpointUp={null}
            headline={headline}
            peekNextSlide={true}
            carouselSettings={{
              variableWidth: false,
              slidesToShow: stackMediaAndCopy ? 4 : 1,
              responsive
            }}
            renderSlide={slide => (
              <Card
                key={slide.id}
                {...slide}
                backgroundColor={backgroundColor}
                backgroundGradient={backgroundGradient}
                stackMediaAndCopy={stackMediaAndCopy}
                centerCopyOnMobile={centerCopyOnMobile}
                hasDescriptionListCheckMark={hasDescriptionListCheckMark}
                hasLargeFontSizes={cardsHaveLargeFontSizes}
                subheadlineColor={subheadlineColor}
              />
            )}
          />
        ) : (
          <>
            {cards && cards.length > 0 && (
              <>
                {cards.map(card => (
                  <Col
                    key={card.id}
                    md={Math.floor(colSpan)}
                    style={{
                      ...(hasOneItemAndStackMedia && {
                        marginRight: 'auto',
                        marginLeft: 'auto'
                      })
                    }}
                  >
                    <Card
                      {...card}
                      backgroundColor={backgroundColor}
                      backgroundGradient={backgroundGradient}
                      stackMediaAndCopy={stackMediaAndCopy}
                      centerCopyOnMobile={centerCopyOnMobile}
                      hasDescriptionListCheckMark={hasDescriptionListCheckMark}
                      hasLargeFontSizes={cardsHaveLargeFontSizes}
                      subheadlineColor={subheadlineColor}
                    />
                  </Col>
                ))}
              </>
            )}
          </>
        )}
      </Row>
    </div>
  )
}

export default CardGroup
